import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitBranch, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { SectionBlock } from "@/components/workspace-settings/SectionBlock";
import {
	getGithubStatus,
	listGithubRepos,
	getProjectRepo,
	setProjectRepo,
	unsetProjectRepo,
	type GithubRepo,
	type ProjectRepo,
} from "@/services/github.service";

export function GithubRepoSection({ projectId }: { projectId: string }) {
	const [loading, setLoading] = useState(true);
	const [connected, setConnected] = useState(false);
	const [repos, setRepos] = useState<GithubRepo[]>([]);
	const [current, setCurrent] = useState<ProjectRepo | null>(null);
	const [selected, setSelected] = useState<string>("");
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const status = await getGithubStatus();
				if (!active) return;
				setConnected(status.configured && status.connected);
				const mapped = await getProjectRepo(projectId);
				if (!active) return;
				setCurrent(mapped);
				if (mapped) setSelected(`${mapped.repo_owner}/${mapped.repo_name}`);
				if (status.configured && status.connected) {
					const list = await listGithubRepos();
					if (active) setRepos(list);
				}
			} catch {
				/* surfaced via empty state */
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [projectId]);

	async function handleSave() {
		const repo = repos.find((r) => `${r.owner}/${r.name}` === selected);
		if (!repo) return;
		setSaving(true);
		try {
			const saved = await setProjectRepo(projectId, {
				installation_id: repo.installation_id,
				repo_owner: repo.owner,
				repo_name: repo.name,
				default_branch: repo.default_branch,
			});
			setCurrent(saved);
			toast.success("Repository linked.");
		} catch (e) {
			toast.error(e instanceof Error ? e.message : "Failed to link repository.");
		} finally {
			setSaving(false);
		}
	}

	async function handleRemove() {
		setSaving(true);
		try {
			await unsetProjectRepo(projectId);
			setCurrent(null);
			setSelected("");
			toast.success("Repository unlinked.");
		} catch (e) {
			toast.error(e instanceof Error ? e.message : "Failed to unlink.");
		} finally {
			setSaving(false);
		}
	}

	return (
		<SectionBlock
			title="GitHub Repository"
			description="Link a repository so tasks in this project can create branches and track pull request status."
		>
			{loading ? (
				<div className="flex items-center gap-2 text-sm text-muted">
					<Loader2 className="h-4 w-4 animate-spin" />
					Loading...
				</div>
			) : !connected ? (
				<p className="text-sm text-muted">
					GitHub is not connected. Connect it in Workspace Settings &gt;
					GitHub Integration first.
				</p>
			) : (
				<div className="flex flex-col gap-3 max-w-xl">
					<SearchableSelect
						value={selected}
						onValueChange={setSelected}
						options={repos.map((r) => ({
							value: `${r.owner}/${r.name}`,
							label: r.full_name,
							description: `default: ${r.default_branch}${r.private ? " · private" : ""}`,
						}))}
						placeholder="Select a repository"
						emptyMessage="No repositories available."
					/>
					<div className="flex items-center gap-2">
						<Button
							onClick={handleSave}
							disabled={saving || !selected || `${current?.repo_owner}/${current?.repo_name}` === selected}
						>
							{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
							<GitBranch className="h-4 w-4 mr-2" />
							{current ? "Update" : "Link repository"}
						</Button>
						{current && (
							<Button
								variant="outline"
								onClick={handleRemove}
								disabled={saving}
							>
								Remove
							</Button>
						)}
					</div>
					{current && (
						<p className="text-xs text-muted">
							Linked to{" "}
							<span className="font-mono text-foreground">
								{current.repo_owner}/{current.repo_name}
							</span>{" "}
							(base: {current.default_branch})
						</p>
					)}
				</div>
			)}
		</SectionBlock>
	);
}
